"use client";

import { useEffect } from "react";
import { reportClientError } from "@/lib/clientErrorReporter";
import { captureException } from "@/lib/posthog";

// Turbopack's "Failed to load chunk <url> from module <id>": the browser could not fetch a JS/CSS chunk (network
// drop, a tab older than the last deploy, a bot that blocks or aborts scripts). The runtime caches that failure for
// the life of the page, so reset() can never fix it; only a fresh page load refetches the chunk.
const isChunkLoadError = (error: Error) => error.message?.startsWith("Failed to load chunk") ?? false;

const CHUNK_RELOAD_KEY = "vr:chunk-reload-at";
const CHUNK_RELOAD_WINDOW_MS = 60_000;

/** Reload once for a chunk-load failure. False if it isn't one, or this tab already reloaded within the window. */
function reloadForChunkError(error: Error): boolean {
  if (!isChunkLoadError(error)) return false;
  try {
    if (Date.now() - Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) < CHUNK_RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    return false; // no storage = no loop guard, so report instead of reloading
  }
  window.location.reload();
  return true;
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // The root layout crashed. Same two sinks as AppErrorBoundary: Cloud Logging
  // `client_error` (pages via the client-error alert) and PostHog Error Tracking.
  useEffect(() => {
    // A one-off chunk failure heals on reload, so it goes to PostHog only: Cloud Logging would page for it. A second
    // failure inside the window falls through and reports, so a broken deploy or CDN still pages.
    if (reloadForChunkError(error)) {
      captureException(error, { source: "boundary", section: "global-error", digest: error.digest, recovery: "reload" });
      return;
    }
    reportClientError({ error, source: "boundary", section: "global-error" });
    captureException(error, { source: "boundary", section: "global-error", digest: error.digest });
  }, [error]);

  return (
    <html>
      <body>
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <h2>Something went wrong</h2>
          <button
            onClick={isChunkLoadError(error) ? () => window.location.reload() : reset}
            style={{
              marginTop: "1rem",
              padding: "0.5rem 1rem",
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  );
}
